import React, { useCallback, useEffect, useRef, useState } from "react";
import { TextField } from "@mui/material";
import { LoadScript } from "@react-google-maps/api";

const libraries = ["places"];

const CityAutocompleteInput = ({
  city,
  onSelect,
  onClear,
  googleLoaded,
}) => {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const lastGeocodedCity = useRef("");
  const onSelectRef = useRef(onSelect);
  const onClearRef = useRef(onClear);

  // Always use the latest callbacks without recreating Google Autocomplete.
  useEffect(() => {
    onSelectRef.current = onSelect;
    onClearRef.current = onClear;
  }, [onSelect, onClear]);

  const geocodeCity = useCallback((cityValue) => {
    const normalizedCity = cityValue?.trim();

    if (!normalizedCity || !window.google?.maps) return;
    if (lastGeocodedCity.current.toLowerCase() === normalizedCity.toLowerCase()) return;

    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode({ address: normalizedCity }, (results, status) => {
      if (status !== "OK" || !results?.length) {
        console.warn("Could not geocode city:", normalizedCity, status);
        return;
      }

      const result = results[0];
      let cityName = "";
      let stateName = "";
      let stateCode = "";
      let latitude = null;
      let longitude = null;

      result.address_components?.forEach((component) => {
        if (
          component.types.includes("locality") ||
          (!cityName && component.types.includes("postal_town"))
        ) {
          cityName = component.long_name;
        }

        if (component.types.includes("administrative_area_level_1")) {
          stateName = component.long_name;
          stateCode = component.short_name;
        }
      });

      if (!cityName) cityName = normalizedCity;

      if (result.geometry?.location) {
        latitude = result.geometry.location.lat();
        longitude = result.geometry.location.lng();
      }

      lastGeocodedCity.current = normalizedCity;

      onSelectRef.current?.({
        city: cityName,
        stateName,
        stateCode,
        latitude,
        longitude,
        fullAddress: result.formatted_address || "",
      });
    });
  }, []);

  // Restore the saved city when the parent loads or changes the profile.
  useEffect(() => {
    if (!inputRef.current) return;

    const savedCity = city || "";

    if (inputRef.current.value !== savedCity) {
      inputRef.current.value = savedCity;
    }

    lastGeocodedCity.current = savedCity.trim();
  }, [city]);

  // Initialize Google Autocomplete once when Google Maps has loaded.
  useEffect(() => {
    if (
      !googleLoaded ||
      !window.google?.maps?.places ||
      !inputRef.current
    ) {
      return;
    }

    const autocomplete = new window.google.maps.places.Autocomplete(
      inputRef.current,
      {
        types: ["(cities)"],
        fields: [
          "address_components",
          "formatted_address",
          "geometry",
          "name",
        ],
      }
    );

    autocompleteRef.current = autocomplete;

    const listener = autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();

      let cityName = "";
      let stateName = "";
      let stateCode = "";
      let latitude = null;
      let longitude = null;

      place.address_components?.forEach((component) => {
        if (
          component.types.includes("locality") ||
          (!cityName && component.types.includes("postal_town"))
        ) {
          cityName = component.long_name;
        }

        if (component.types.includes("administrative_area_level_1")) {
          stateName = component.long_name;
          stateCode = component.short_name;
        }
      });

      if (!cityName && place.name) {
        cityName = place.name;
      }

      if (place.geometry?.location) {
        latitude = place.geometry.location.lat();
        longitude = place.geometry.location.lng();
      }

      lastGeocodedCity.current = cityName.trim();

      // Keep the input text consistent with the selected place.
      if (inputRef.current && cityName) {
        inputRef.current.value = cityName;
      }

      onSelectRef.current?.({
        city: cityName,
        stateName,
        stateCode,
        latitude,
        longitude,
        fullAddress: place.formatted_address || "",
      });
    });

    return () => {
      listener.remove();
      autocompleteRef.current = null;
    };
  }, [googleLoaded]);

  const handleCityChange = (event) => {
    const value = event.target.value;
    const normalizedValue = value.trim();

    if (
      normalizedValue.toLowerCase() !==
      lastGeocodedCity.current.toLowerCase()
    ) {
      lastGeocodedCity.current = "";
    }

    // Clear dependent location data when the user empties the field.
    if (!normalizedValue) {
      lastGeocodedCity.current = "";
      onClearRef.current?.();
    }
  };

  const handleBlur = () => {
    const currentCity = inputRef.current?.value?.trim() || "";

    if (currentCity) {
      geocodeCity(currentCity);
    }
  };

  return (
    <TextField
      label="City"
      fullWidth
      inputRef={inputRef}
      placeholder="Start typing city..."
      InputLabelProps={{ shrink: true }}
      onChange={handleCityChange}
      onBlur={handleBlur}
    />
  );
};

export default function CityAutocomplete(props) {
  const [googleLoaded, setGoogleLoaded] = useState(false);

  return (
    <LoadScript
      googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
      libraries={libraries}
      onLoad={() => {
        console.log("Google Maps loaded");
        setGoogleLoaded(true);
      }}
    >
      <CityAutocompleteInput
        {...props}
        googleLoaded={googleLoaded}
      />
    </LoadScript>
  );
}